import React from 'react';
import RefundStatusBadge from './RefundStatusBadge';

const RefundDetailsSection = ({ refundStatus }) => {
  if (!refundStatus) return null;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-4 rounded">
      <div className="flex justify-between items-start mb-3">
        <div>
          <h4 className="font-semibold text-amber-900 mb-1">Refund Request</h4>
          <p className="text-sm text-amber-700">
            Status: <span className="font-bold">{refundStatus.status}</span>
          </p>
        </div>
        <RefundStatusBadge refundStatus={refundStatus} />
      </div>

      {/* Refund Amount Details */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-4">
        <div>
          <p className="text-amber-600 text-xs font-semibold">Original Amount</p>
          <p className="font-bold text-amber-900">${parseFloat(refundStatus.original_amount || 0).toFixed(2)}</p>
        </div>
        <div>
          <p className="text-amber-600 text-xs font-semibold">Refund Amount</p>
          <p className="font-bold text-amber-900">${parseFloat(refundStatus.refund_amount || 0).toFixed(2)}</p>
        </div>
        <div>
          <p className="text-amber-600 text-xs font-semibold">Cancellation Fee</p>
          <p className="font-bold text-red-600">${(parseFloat(refundStatus.original_amount || 0) - parseFloat(refundStatus.refund_amount || 0)).toFixed(2)}</p>
        </div>
        <div>
          <p className="text-amber-600 text-xs font-semibold">Approvals</p>
          <p className="font-bold text-amber-900">
            {refundStatus.approvals_received}/{refundStatus.approvals_required}
          </p>
        </div>
      </div>

      {/* Timeline */}
      <div className="space-y-2 text-sm mb-4">
        <div className="flex items-center gap-2">
          <i className="fa fa-calendar text-amber-600 w-4"></i>
          <span className="text-amber-700">
            Requested: <span className="font-semibold">{formatDate(refundStatus.requested_at)}</span>
          </span>
        </div>

        {refundStatus.approved_at && (
          <div className="flex items-center gap-2">
            <i className="fa fa-check-circle text-green-600 w-4"></i>
            <span className="text-green-700">
              Approved: <span className="font-semibold">{formatDate(refundStatus.approved_at)}</span>
            </span>
          </div>
        )}

        {refundStatus.completed_at && (
          <div className="flex items-center gap-2">
            <i className="fa fa-check-double text-green-600 w-4"></i>
            <span className="text-green-700">
              Completed: <span className="font-semibold">{formatDate(refundStatus.completed_at)}</span>
            </span>
          </div>
        )}
      </div>

      {/* Rejection — only when the refund request is finally rejected (not interim SubAdmin no on dual approval) */}
      {refundStatus.status === 'Rejected' && (
        <div className="mt-3 pt-3 border-t border-amber-200">
          {refundStatus.final_rejection ? (
            <div className="text-xs text-red-700 mb-2 bg-red-50 p-2 rounded border border-red-200">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-semibold">
                  <i className="fa fa-times-circle text-red-600 mr-1"></i>
                  Rejected by {refundStatus.final_rejection.approval_type}
                </span>
              </div>
              <p className="text-red-600">Reason: {refundStatus.final_rejection.reason}</p>
              {refundStatus.final_rejection.approved_at && (
                <p className="text-red-500 text-xs mt-1">
                  Rejected at: {formatDate(refundStatus.final_rejection.approved_at)}
                </p>
              )}
            </div>
          ) : (
            refundStatus.approvals
              ?.filter((approval) => approval.approval_status === 'Rejected')
              .map((rejection, idx) => (
                <div key={idx} className="text-xs text-red-700 mb-2 bg-red-50 p-2 rounded border border-red-200">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold">
                      <i className="fa fa-times-circle text-red-600 mr-1"></i>
                      Rejected
                    </span>
                  </div>
                  <p className="text-red-600">Reason: {rejection.reason}</p>
                  <p className="text-red-500 text-xs mt-1">Rejected at: {formatDate(rejection.approved_at)}</p>
                </div>
              ))
          )}
        </div>
      )}

      {refundStatus.status === 'Failed' && (
        <div className="mt-3 pt-3 border-t border-amber-200">
          <p className="text-xs text-red-700 bg-red-50 p-2 rounded border border-red-200">
            <i className="fa fa-exclamation-circle text-red-600 mr-1"></i>
            This refund was approved but the payment could not be sent to Stripe. An admin can retry from Refund History.
          </p>
        </div>
      )}

      {/* Completion Message */}
      {refundStatus.status === 'Completed' && refundStatus.completed_at && (
        <div className="mt-3 pt-3 border-t border-amber-200">
          <p className="text-xs text-green-700 flex items-center gap-2">
            <i className="fa fa-check-circle"></i>
            Refund completed on {formatDate(refundStatus.completed_at)}
          </p>
        </div>
      )}
    </div>
  );
};

export default RefundDetailsSection;
